import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ChooseLocationService } from '../services';
import {
  DeliveryLocationAction,
  DeliveryLocationErrorAction,
  DeliveryLocationSuccessAction,
  getChooseLocationAction,
  getChooseLocationErrorAction,
  getChooseLocationSuccessAction,
  getLocationMenusAction,
  getLocationMenusErrorAction,
  getLocationMenusSuccessAction,
  getLocationTimeslotAction,
  getLocationTimeslotErrorAction,
  getLocationTimeslotSuccessAction,
  setLocationPropTypes,
  SetSelectedLocationAction,
} from './choose-location.actions';
import { ChooseLocationFacade } from './choose-location.facade';

@Injectable()
export class ChooseLocationEffects {
  constructor(
    private action$: Actions,
    private chooseLocationService: ChooseLocationService,
    private chooseLocationFacade: ChooseLocationFacade
  ) {}

  protected getChooseLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(getChooseLocationAction),
      switchMap(() => {
        return this.chooseLocationService.getChooseLocation().pipe(
          tap((data) => {
            this.chooseLocationFacade.chooseLocationBehavior.next(data);
          }),
          map((chooseLocation: any) => getChooseLocationSuccessAction({ chooseLocation })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting location', ApiResponseCode.Server_Error))
              .build();
            return of(getChooseLocationErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getLocationTimeSlot$ = createEffect(() =>
    this.action$.pipe(
      ofType(getLocationTimeslotAction),
      switchMap((payload) => {
        return this.chooseLocationService.getLocationTimeslots(payload).pipe(
          map((locationTimeslot: any) => getLocationTimeslotSuccessAction({ locationTimeslot })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting locationTimeslot', ApiResponseCode.Server_Error))
              .build();
            return of(getLocationTimeslotErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getLocationMenus$ = createEffect(() =>
    this.action$.pipe(
      ofType(getLocationMenusAction),
      withLatestFrom(this.chooseLocationFacade.selectedLocation$, this.chooseLocationFacade.selectedTimeslot$),
      switchMap(([action, selectedLocation, selectedTimeslot]) => {
        const payload = {
          locationID: selectedLocation.id,
          orderDate: selectedTimeslot.start,
          periodID: selectedTimeslot.periodID,
        };

        return this.chooseLocationService.getLocationMenus(payload).pipe(
          map((locationMenus: any) => getLocationMenusSuccessAction({ locationMenus })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting locationMenus', ApiResponseCode.Server_Error))
              .build();
            return of(getLocationMenusErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected deliveryLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(DeliveryLocationAction),
      switchMap((payload) => {
        return this.chooseLocationService.getDeliveryLocation(payload).pipe(
          map((deliveryLocation: any) => DeliveryLocationSuccessAction({ deliveryLocation })),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting deliveryLocation', ApiResponseCode.Server_Error))
              .build();
            return of(DeliveryLocationErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected setSelectedLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(SetSelectedLocationAction),
      switchMap(({ selectedLocation }) => {
        this.chooseLocationService.setLocationPropTypesIndex();
        if (selectedLocation.extendedProperties) {
          selectedLocation.extendedProperties.map((val) => {
            if (this.chooseLocationService.locationPropTypesIndex[val.propType]) {
              this.chooseLocationService.locationPropTypesIndex[val.propType].value = val.value;
            }
          });
          return of(setLocationPropTypes({ locationPropTypes: this.chooseLocationService.locationPropTypesIndex }));
        }
      })
    )
  );
}
